import { useState } from "react";
import { Link } from "react-router";
import { actionLabel, groupByOffer, shortDay, sourceBadge, spendProgress, type OfferRow } from "../lib/offersPage";
import { formatExact } from "../lib/money";
import { toneClass } from "../lib/tone";
import { Icon } from "./Icons";
import { OfferText } from "./OfferText";

/** Same offer details and per-card actions in the portfolio and card sheet. */
export function TrackedOfferList({ offers, isBusy, onToggleUsed, onTogglePinned, onDelete }: {
  offers: OfferRow[];
  isBusy: (id: number) => boolean;
  onToggleUsed: (offer: OfferRow) => void;
  onTogglePinned: (offers: OfferRow[]) => void;
  onDelete: (offer: OfferRow) => void;
}) {
  return <ul className="divide-y divide-neutral-800">{groupByOffer(offers).map(group =>
    <OfferItem key={group.key} offers={group.offers} showCards isBusy={isBusy}
      onToggleUsed={onToggleUsed} onTogglePinned={() => onTogglePinned(group.offers)} onDelete={onDelete} />
  )}</ul>;
}

export function OfferItem({
  offers,
  showCards,
  isBusy,
  onToggleUsed,
  onTogglePinned,
  onDelete,
}: {
  offers: OfferRow[];
  showCards: boolean;
  isBusy: (id: number) => boolean;
  onToggleUsed: (o: OfferRow) => void;
  onTogglePinned: () => void;
  onDelete: (o: OfferRow) => void;
}) {
  const o = offers[0];
  const single = offers.length === 1;
  const progress = single ? spendProgress(o) : null;
  const badge = sourceBadge(o.source);
  const gone = offers.every((x) => Boolean(x.missing_since));
  const used = offers.every((x) => x.used);
  const pinned = offers.some((x) => Boolean(x.pinned));
  const busy = offers.some((x) => isBusy(x.id));
  const enrolledOn = offers.filter((x) => x.enrolled);
  const notEnrolledOn = offers.filter((x) => !x.enrolled);
  const [open, setOpen] = useState(gone);
  // Checked before it is rendered: a date this page cannot read would leave
  // "Gone " dangling, which reads as a bug rather than a missing fact.
  const goneOn = shortDay(o.missing_since);
  return (
    <li className="text-sm">
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-start gap-x-2 py-2 pr-3 pl-1.5">
        <button
          type="button"
          disabled={busy}
          onClick={onTogglePinned}
          aria-pressed={pinned}
          aria-label={`${pinned ? "Unpin" : "Pin"} ${o.merchant}`}
          title={pinned ? "Unpin: stop reading it on the podcast" : "Pin: read it on the podcast as it nears expiry"}
          className={`grid h-9 w-9 place-items-center rounded-lg hover:bg-neutral-800 disabled:opacity-50 ${
            pinned ? "text-accent" : "text-neutral-500"
          }`}
        >
          <Icon name="pin" size={17} strokeWidth={1.6} className={pinned ? "fill-current" : ""} />
        </button>
        <div className="min-w-0 pt-1">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="block w-full min-w-0 text-left"
          >
            <span className={`flex flex-wrap items-baseline gap-x-2 ${used ? "line-through opacity-60" : ""}`}>
              <strong className="text-[15px]">{o.merchant}</strong>
              <span className="font-mono text-[13px] tabular-nums text-neutral-200">{o.reward}</span>
            </span>
            {showCards && (
              <span className="flex flex-wrap items-center gap-1 text-[12px] text-neutral-500">
                {enrolledOn.length > 0 && <CardList label="Added on" rows={enrolledOn} />}
                {notEnrolledOn.length > 0 && (
                  <CardList label="Not yet added on" rows={notEnrolledOn} warn />
                )}
              </span>
            )}
            {o.title && (
              <span className="block truncate text-[13px] text-neutral-400">{o.title}</span>
            )}
          </button>
          {!showCards && notEnrolledOn.length > 0 && (
            <span className="mt-1 mr-1.5 inline-block rounded-full border border-amber-900/70 px-2 text-xs leading-5 text-amber-300">
              Not yet enrolled
            </span>
          )}
          {/* The section header promises a podcast reading; say so where the
              feed skips a pinned row (unenrolled or no end date). */}
          {pinned && !used && !o.expired && offers.every((x) => x.read_on_podcast === false) && (
            <span className="mt-1 inline-block rounded-full border border-neutral-700 px-2 text-xs leading-5 text-neutral-400">
              Not on the podcast{enrolledOn.length > 0 ? ": no end date" : ""}
            </span>
          )}
          {progress && <SpendBar progress={progress} />}
        </div>
        <span
          className={`shrink-0 pt-1.5 text-right font-mono text-xs whitespace-nowrap ${
            gone ? "text-neutral-500" : toneClass(o.expiry[0]) || "text-neutral-500"
          }`}
        >
          {gone ? (goneOn ? `Gone ${goneOn}` : "No longer listed") : o.expiry[1]}
        </span>
      </div>
      {open && (
        <div className="space-y-2 px-3 pb-3 sm:pl-[3.25rem]">
          <OfferText offer={o} />
          <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-neutral-500">
            {/* Falsy covers null and 0 alike: a zero threshold means no spend
                is required, and "min spend $0.00" states a condition that does
                not exist. */}
            {o.spend_threshold ? (
              <span>min spend {formatExact(o.spend_threshold)}</span>
            ) : null}
            {single && <CardFacts o={o} />}
            {badge && (
              <span className={badge.automatic ? "text-accent" : ""}>{badge.label}</span>
            )}
          </div>
          {o.notes && <div className="text-xs text-neutral-500">{o.notes}</div>}
          {single ? (
            <CardActions o={o} busy={busy} onToggleUsed={onToggleUsed} onDelete={onDelete} />
          ) : (
            <ul aria-label={`${o.merchant} by card`} className="divide-y divide-neutral-800 rounded-lg border border-neutral-800">
              {offers.map((x) => {
                const p = spendProgress(x);
                return (
                  <li key={x.id} className="space-y-1 px-2.5 py-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={`text-[13px] ${x.used ? "line-through opacity-60" : ""}`}>
                        {x.card_name}
                      </span>
                      {!x.enrolled && <span className="text-xs text-amber-300">not yet enrolled</span>}
                      {x.used && <span className="text-xs text-neutral-500">used</span>}
                      <span className="flex flex-wrap gap-x-3 text-xs text-neutral-500">
                        <CardFacts o={x} />
                      </span>
                    </div>
                    {p && <SpendBar progress={p} />}
                    <CardActions o={x} busy={isBusy(x.id)} onToggleUsed={onToggleUsed} onDelete={onDelete} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

/** "Added on" and the cards, each as a chip. */
function CardList({ label, rows, warn = false }: { label: string; rows: OfferRow[]; warn?: boolean }) {
  return (
    <>
      <span className={warn ? "text-amber-300" : ""}>{label}</span>
      {rows.map((x) => (
        <span
          key={x.id}
          className={`rounded px-1.5 text-[11.5px] ${
            warn ? "border border-amber-900/70 text-amber-300" : "bg-neutral-800 text-neutral-300"
          }`}
        >
          {x.card_name}
        </span>
      ))}
    </>
  );
}

function SpendBar({ progress }: { progress: NonNullable<ReturnType<typeof spendProgress>> }) {
  return (
    <div
      role="progressbar"
      aria-label="Spend progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress.fraction * 100)}
      className="mt-1.5 h-1 max-w-[260px] overflow-hidden rounded-full bg-neutral-800"
    >
      <div
        className={`h-full ${progress.remaining > 0 ? "bg-sky-500" : "bg-emerald-500"}`}
        style={{ width: `${progress.fraction * 100}%` }}
      />
    </div>
  );
}

/** What one card's copy says for itself: spend so far and when it was last listed. */
function CardFacts({ o }: { o: OfferRow }) {
  const progress = spendProgress(o);
  const seenOn = shortDay(o.last_seen_at);
  return (
    <>
      {o.card_id != null && <Link to={`/cards?card=${o.card_id}&cardTab=offers`} className="text-accent underline">{o.card_name}</Link>}
      {progress && (
        <span className="tabular-nums text-neutral-400">
          {formatExact(o.spend_progress ?? 0)} of {formatExact(o.spend_threshold ?? 0)} spent
          {progress.remaining > 0
            ? ` · ${formatExact(progress.remaining)} to go`
            : " · requirement met"}
          {o.progress_as_of && ` · as of ${o.progress_as_of}`}
        </span>
      )}
      {o.missing_since && seenOn && <span>last seen {seenOn}</span>}
    </>
  );
}

function CardActions({ o, busy, onToggleUsed, onDelete }: {
  o: OfferRow;
  busy: boolean;
  onToggleUsed: (o: OfferRow) => void;
  onDelete: (o: OfferRow) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={() => onToggleUsed(o)}
        className="btn btn-ghost btn-sm"
      >
        {actionLabel(o.action)}
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => onDelete(o)}
        className="btn btn-sm ml-auto text-red-400"
      >
        Delete
      </button>
    </div>
  );
}
